/**
 * Placement decision for the habits board info popover (full habit name +
 * description). The popover is anchored to the row's name column; it should
 * never overflow the scrollable `.board-wrap` container — overflowing would
 * both clip the popover (hidden below the last rows) and make the container
 * itself scrollable. When there isn't room below the row, we flip the popover
 * above it; when neither side has room, we clamp its max-height so it scrolls
 * internally instead.
 *
 * Fits-checks require the popover's 6px gap plus a 2px buffer on top of the
 * content height, so exact-boundary cases never overflow. The clamp subtracts
 * the same gap + buffer and applies no floor — a floor would let the popover
 * exceed the available space and overflow by design.
 */

export type InfoPlacement = 'below' | 'above';

export interface InfoPlacementMetrics {
  /**
   * Natural rendered height of the popover (already capped by the CSS
   * max-height). Omit when the height isn't available yet (e.g. the space
   * below is roomy enough that the height doesn't matter); it then assumes
   * the worst case (the max height).
   */
  popoverHeight?: number;
  /** Space (px) between the row's bottom edge and the container's bottom edge. */
  availableBelow: number;
  /** Space (px) between the container's top edge and the row's top edge. */
  availableAbove: number;
}

/** Matches the CSS gap in `.habit-info` (top/bottom: calc(100% + 6px)). */
export const INFO_POPOVER_GAP_PX = 6;
/** Matches the CSS max-height of `.habit-info`. */
export const INFO_POPOVER_MAX_HEIGHT_PX = 240;
/** Subpixel-rounding safety margin; also subtracted from the clamped height. */
export const INFO_POPOVER_FIT_BUFFER_PX = 2;

export function pickInfoPlacement({
  popoverHeight,
  availableBelow,
  availableAbove,
}: InfoPlacementMetrics): { placement: InfoPlacement; maxHeight: number } {
  // Fits checks use the actual height (capped at the CSS max) so a short
  // popover can stay below even when the space is small.
  const needed = Math.min(popoverHeight ?? INFO_POPOVER_MAX_HEIGHT_PX, INFO_POPOVER_MAX_HEIGHT_PX);
  // The popover renders GAP px away from the row, so that gap must fit too.
  const fitSlack = INFO_POPOVER_GAP_PX + INFO_POPOVER_FIT_BUFFER_PX;

  if (availableBelow >= needed + fitSlack) {
    // Room below — the natural rendering already fits, keep the CSS default.
    return { placement: 'below', maxHeight: INFO_POPOVER_MAX_HEIGHT_PX };
  }
  if (availableAbove >= needed + fitSlack) {
    // Not enough room below (near the bottom of the board): flip above so the
    // popover stays inside the container instead of overflowing it.
    return { placement: 'above', maxHeight: INFO_POPOVER_MAX_HEIGHT_PX };
  }

  // Neither side fits fully: use the roomier side and clamp the popover's
  // height so it (plus the gap + buffer) always fits. No floor — any floor
  // larger than the available space would overflow by design.
  const fit = (space: number) =>
    Math.max(
      0,
      Math.min(
        INFO_POPOVER_MAX_HEIGHT_PX,
        Math.floor(space - INFO_POPOVER_GAP_PX - INFO_POPOVER_FIT_BUFFER_PX),
      ),
    );
  if (availableAbove >= availableBelow) {
    return { placement: 'above', maxHeight: fit(availableAbove) };
  }
  return { placement: 'below', maxHeight: fit(availableBelow) };
}
